/**
 * The Calibration sheet's identity fields — port of OG `CalibAndABIDSupport` (SpreadsheetEntryController
 * :15864). OG writes the template's CalibrationTable fields that DataParsing marks Read (not Continuous)
 * into the sheet named "Calibration": the batch's Calibration ID (Phosphate 8076: G2) and Calibrated By
 * (G8, the current user's name — OG's calibration data source returns `@USERNAME`).
 *
 * AI rebuilds the sheet from the template on every open, so these are written here, into the workbook,
 * before it is shown — in View mode too. The ID is the batch's linked calibration (created on the batch's
 * first save, AI_Analysis_Sdms_BatchCalibration_Ensure_SP). (user report 2026-10-12)
 */
import type ExcelJS from 'exceljs';

/** OG targets the calibration sheet by NAME (the merge may move it, so an index is unreliable). */
export const CALIBRATION_SHEET = 'Calibration';

/** Fields the batch owns: always written when a value is known, replacing what the sheet shows. Every other
 *  field (Calibrated By, …) is only filled when its cell is still empty, so a saved value is kept. */
const AUTHORITATIVE = new Set(['CALIBRATIONID']);

/**
 * Write `values` (field → text, upper-cased keys) at `cells` (field → A1) on the "Calibration" sheet.
 * A blank value is never written; a formula cell is never overwritten. Returns the number of cells written.
 */
export function applyCalibrationFieldsToWorkbook(
  wb: ExcelJS.Workbook,
  cells: Readonly<Record<string, string>> | null | undefined,
  values: Readonly<Record<string, string | null | undefined>>,
): number {
  if (!cells) return 0;
  const ws = wb.getWorksheet(CALIBRATION_SHEET);
  if (!ws) return 0;
  let written = 0;
  for (const [rawField, addr] of Object.entries(cells)) {
    const field = rawField.toUpperCase();
    const value = values[field];
    if (value == null || String(value).trim() === '' || !addr) continue;
    const cell = ws.getCell(addr);
    if (typeof cell.formula === 'string' && cell.formula) continue;
    const current = cell.value == null ? '' : String(cell.text ?? cell.value).trim();
    if (!AUTHORITATIVE.has(field) && current !== '') continue;
    if (current === String(value)) continue;
    cell.value = String(value);
    written++;
  }
  return written;
}

/** Where the Calibration ID goes in the live sheet (0-based), for writing a newly created ID after a Save —
 *  null when the template maps no CALIBRATIONID cell. */
export function calibrationIdCell(
  cells: Readonly<Record<string, string>> | null | undefined,
): { sheetName: string; row: number; col: number } | null {
  const addr = cells?.CALIBRATIONID ?? cells?.CalibrationID;
  const m = /^\$?([A-Z]{1,3})\$?(\d+)$/i.exec((addr ?? '').trim());
  if (!m) return null;
  let col = 0;
  for (const ch of m[1]!.toUpperCase()) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { sheetName: CALIBRATION_SHEET, row: Number(m[2]) - 1, col: col - 1 };
}
